import { format } from 'date-fns';
import {
  DelayReasonOption,
  DEFAULT_DELAY_REASONS,
  getDelayReasonLabel,
} from '@/components/pages/kiosk/tablet/v2/DelayReasonModal';

export type ScheduledProcedureRef = {
  id: number | string;
  startTime?: string | null;
};

export type DelayReasonProcedureRef = {
  id?: number | string | null;
  orId?: number | string | null;
  startTime?: string | null;
};

/**
 * Patient shapes that carry a procedure. Kiosk screens run patients through
 * `mapPatientData`, which replaces `procedure` with the procedure-type name and
 * moves the procedure object to `procedureData`; web views keep `procedure`.
 */
export type DelayReasonPatientRef = {
  id?: number | string | null;
  name?: string | null;
  procedure?: DelayReasonProcedureRef | string | null;
  procedureData?: DelayReasonProcedureRef | null;
};

export const resolvePatientProcedure = (
  patient: DelayReasonPatientRef | null | undefined
): DelayReasonProcedureRef | null => {
  const candidate = patient?.procedure;
  if (candidate && typeof candidate === 'object') {
    return candidate;
  }
  return patient?.procedureData ?? null;
};

/**
 * First case of the day in an OR. Mirrors the server's `isDelayReasonRequired`
 * check, which lists the OR's non-canceled procedures for the scheduled date
 * ordered by start time and treats the head as the first case.
 */
export const isFirstScheduledProcedure = (
  procedureId: number | string | null | undefined,
  procedures: ScheduledProcedureRef[]
): boolean => {
  if (procedureId == null || procedures.length === 0) {
    return false;
  }

  const sorted = [...procedures].sort((left, right) => {
    const leftTime = left.startTime ? new Date(left.startTime).getTime() : Number.POSITIVE_INFINITY;
    const rightTime = right.startTime ? new Date(right.startTime).getTime() : Number.POSITIVE_INFINITY;
    if (leftTime !== rightTime) {
      return leftTime - rightTime;
    }
    return String(left.id).localeCompare(String(right.id));
  });

  return String(sorted[0]?.id) === String(procedureId);
};

/**
 * Same rule the OR iPad prompt follows on the server: the case must be the
 * first of the day in its OR and past the FCOTS grace period (the server only
 * returns `delayStartedAt` in that situation, including for patients who have
 * not rolled into the OR yet). `required` is the server's own confirmation of
 * that rule, so it always enables; it is only true on machines whose screen
 * config turns the OR prompt on, which is why the client check exists.
 */
export const isProcedureDelayEnabled = ({
  delayStartedAt,
  isFirstCaseOfDay,
  delayReasonRequired = false,
}: {
  delayStartedAt?: string | null;
  isFirstCaseOfDay: boolean;
  delayReasonRequired?: boolean;
}): boolean => delayReasonRequired || (isFirstCaseOfDay && Boolean(delayStartedAt));

/**
 * Delay start recorded on a new reason, in the same order the OR iPad uses:
 * the server's computed grace-period end, then the scheduled start of the
 * procedure. Never "now", which would misstate retroactive entries.
 */
export const resolveDelayStartedAt = ({
  serverDelayStartedAt,
  procedureStartTime,
}: {
  serverDelayStartedAt?: string | null;
  procedureStartTime?: string | null;
}): string | null => serverDelayStartedAt ?? procedureStartTime ?? null;

export const scheduleDateForProcedure = (startTime: string | null | undefined, now: Date): Date => {
  if (!startTime) {
    return now;
  }
  const parsed = new Date(startTime);
  return Number.isNaN(parsed.getTime()) ? now : parsed;
};

export const formatProcedureScheduleDate = (startTime: string | null | undefined, now = new Date()): string =>
  format(scheduleDateForProcedure(startTime, now), 'YYYY-MM-DD');

export const parseStoredProcedureDelayReason = (
  reason?: string | null
): { id: string; description?: string } | null => {
  if (!reason) {
    return null;
  }
  const separator = reason.indexOf(':');
  if (separator === -1) {
    return { id: reason };
  }
  const id = reason.slice(0, separator);
  const description = reason.slice(separator + 1);
  return { id, description: description || undefined };
};

export const formatStoredProcedureDelayReason = (id: string, description?: string): string =>
  description ? `${id}:${description}` : id;

export const procedureDelayReasonLabel = (
  reason: string | null | undefined,
  options: DelayReasonOption[]
): string | undefined => {
  const parsed = parseStoredProcedureDelayReason(reason);
  if (!parsed) {
    return undefined;
  }
  if (parsed.id.toLowerCase() === 'other' && parsed.description) {
    return parsed.description;
  }
  return getDelayReasonLabel(parsed.id, options);
};

type DelayReasonConfig = { values?: string | null };

export const configuredProcedureDelayReasons = (configs: DelayReasonConfig[] | undefined): DelayReasonOption[] => {
  const list = configs ?? [];
  if (list.length === 0) {
    return [...DEFAULT_DELAY_REASONS];
  }

  const reasons: DelayReasonOption[] = [];
  for (const config of list) {
    try {
      const parsed = JSON.parse(config.values || '{}');
      const name = typeof parsed?.name === 'string' && parsed.name.trim() ? parsed.name.trim() : 'Unknown';
      reasons.push({ id: name, label: name });
    } catch {
      reasons.push({ id: 'Unknown', label: 'Unknown' });
    }
  }

  if (!reasons.some(reason => reason.id.toLowerCase() === 'other')) {
    reasons.push({ id: 'other', label: 'Other' });
  }
  return reasons;
};
